import { Form } from 'antd';
import type { FormInstance } from 'antd/lib/form';
import axios from 'axios';
import i18n from 'i18next';
import { sortBy } from 'lodash';
import moment from 'moment';
import { type FunctionComponent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { jsonToCSV } from 'react-papaparse';

import { useUploadContext } from 'components/FileUploader';
import { Spinner, useMessage } from 'components/ui';
import {
  type AtlasGqlDamageSchema,
  useCompleteUploadMutation,
  useCreateSignedUploadMutation,
  useGetDamageSchemasForIngestionQuery,
} from 'types/atlas-graphql';

import {
  type DataField,
  type EditableCsvDamageRow,
  type HeaderMappingEntry,
  type ImportResult,
  type SchemaOptions,
} from '../types';

import { STEPS } from './common';
import { Confirmation } from './Confirmation';
import { ErrorState } from './ErrorState';
import { UploadForm } from './UploadForm';
import {
  type DamageSchemaForValidation,
  isDamageLookupSchemaDefinition,
  validateCsvDamageRows,
} from './validation';

type ModalContentProps = {
  inspectionId?: string;
  multiInspection?: boolean;
  setModalOpen: (open: boolean) => void;
  step: number;
  setStep: (step: number) => void;
  form: FormInstance;
  handleCheckStatus: (bundleId: string) => void;
  onReset: () => void;
  importResult: ImportResult | undefined;
  ErrorStateComponent?: FunctionComponent<{
    importResult: ImportResult | undefined;
    importedFileName: string;
  }>;
};

const INSPECTION_DAMAGE_DATA_TYPE = 'InspectionDamage';

const commonDataFields: DataField[] = [
  {
    key: 'IMAGE_NAME',
    label: i18n.t('inspection.photoFileName', 'Photo File Name'),
    required: true,
  },
  {
    key: 'COORDINATES',
    label: i18n.t('inspection.coordinates', 'Coordinates'),
    required: true,
  },
];

const assetLookupFields: DataField[] = [
  {
    key: 'INSPECTION_DATE',
    label: i18n.t('inspection.inspectionDate', 'Inspection Date'),
    required: true,
  },
  {
    key: 'SITE_NAME',
    label: i18n.t('table.site', 'Site'),
    required: true,
  },
  {
    key: 'TURBINE_NAME',
    label: i18n.t('table.turbine', 'Turbine'),
    required: true,
  },
  {
    key: 'COMPONENT_UNIQUE_NAME',
    label: i18n.t('inspection.componentName', 'Component Name'),
    required: false,
  },
];

export const ModalContent = ({
  inspectionId,
  setModalOpen,
  step,
  setStep,
  form,
  handleCheckStatus,
  onReset,
  importResult,
  multiInspection,
  ErrorStateComponent,
}: ModalContentProps) => {
  const handleResetAndClose = () => {
    setStep(STEPS.upload);
    setModalOpen(false);
    setRows([]);
    onReset();
  };
  const targetInspectionId = !!inspectionId && !multiInspection ? inspectionId : undefined;

  const message = useMessage();
  const { t } = useTranslation();
  const [headerMapping, setHeaderMapping] = useState<HeaderMappingEntry[]>([]);

  const commonFields = useMemo(
    () => [...commonDataFields, ...(!targetInspectionId ? assetLookupFields : [])],
    [targetInspectionId]
  );

  const [dataFields, setDataFields] = useState<DataField[]>(commonFields);
  const [rows, setRows] = useState<EditableCsvDamageRow[]>([]);

  const { selectedFiles, setSelectedFiles, setUploading, uploading } = useUploadContext();

  const { data: damageSchemaData, loading } = useGetDamageSchemasForIngestionQuery();

  const schemaOptions: SchemaOptions[] =
    damageSchemaData?.damageSchemas.map(({ id, name }) => ({ value: id, label: name ?? id })) ?? [];

  const defaultSchema = damageSchemaData?.damageSchemas?.find(({ isDefault }) => isDefault);

  const getSelectedSchema = (): DamageSchemaForValidation | undefined => {
    const { damageSchemaId } = form.getFieldsValue(['damageSchemaId']);

    return damageSchemaData?.damageSchemas?.find(({ id }) => id === damageSchemaId) as
      | DamageSchemaForValidation
      | undefined;
  };

  const setFieldsForSchema = useCallback(
    (schema: AtlasGqlDamageSchema) => {
      const fieldsForSchema: DataField[] = sortBy(
        (schema?.definitions ?? []).filter(
          definition => !isDamageLookupSchemaDefinition(definition)
        ),
        'sortIndex'
      ).map(({ id, name }) => ({ key: id, label: name }));

      setDataFields([...commonFields, ...fieldsForSchema]);

      // TODO: attempt to map fields automatically
    },
    [commonFields]
  );

  useEffect(() => {
    setFieldsForSchema(defaultSchema as AtlasGqlDamageSchema);
  }, [defaultSchema, setFieldsForSchema]);

  const handleClickPreview = async () => {
    try {
      await form.validateFields();
    } catch (errorInfo) {
      if (errorInfo.errorFields && errorInfo.errorFields.length > 0) {
        form.scrollToField(errorInfo.errorFields[0].name, {
          behavior: 'smooth',
          block: 'center',
        });
      }
      return;
    }

    const values = form.getFieldsValue(true);
    const nextHeaderMapping = Object.entries(values)
      .filter(
        ([mappedKey, value]: [string, string]) =>
          value && dataFields?.find(({ key }) => key === mappedKey)
      )
      .map(([key, value]: [string, string]) => {
        return { fileHeader: value, schemaFieldId: key };
      });

    setHeaderMapping(nextHeaderMapping);
    setRows(
      validateCsvDamageRows({
        rows,
        dataFields,
        headerMapping: nextHeaderMapping,
        schema: getSelectedSchema(),
      })
    );

    setStep(STEPS.confirmation);
  };

  const handleFileUpload = async (url: string): Promise<void> => {
    const csv = jsonToCSV(rows.map(row => row.values));
    await axios.put(url, csv);
  };

  const [createSignedUpload] = useCreateSignedUploadMutation();

  const [completeUpload] = useCompleteUploadMutation();

  const handleSubmit = async () => {
    if (uploading) {
      return;
    }
    const values = form.getFieldsValue(['damageSchemaId', 'file']);
    const fileName: string = values.file.file.name;

    setUploading(true);

    setSelectedFiles([
      { name: fileName, path: '', percent: 0, sourceName: fileName, type: 'file', valid: true },
    ]);

    const bundleId = moment().format('x');

    const input = {
      bundleId,
      recordId: targetInspectionId ? targetInspectionId : bundleId,
      dataType: INSPECTION_DAMAGE_DATA_TYPE,
      files: [{ fileName }],
      params: {
        inspectionId: targetInspectionId,
        multiInspection: !targetInspectionId,
        headerMapping,
        damageSchemaId: values.damageSchemaId,
      },
    };

    const { data: createSignedUploadData } = await createSignedUpload({ variables: { input } });

    const { url } = createSignedUploadData?.createSignedUpload?.[0] ?? {};

    if (url) {
      try {
        await handleFileUpload(url);
        await completeUpload({ variables: { input } });
      } catch (error) {
        console.error(error);
        message.error(t('inspection.errorUploading', 'Error uploading. Please try again.'));
      }
      setUploading(false);
      handleCheckStatus(bundleId);
      onReset();
    }

    handleResetAndClose();
  };

  const handleFieldChange = (changedFields: { name: string[]; value: any }[]) => {
    const damageSchemaIdField = changedFields.find(({ name }) => name.includes('damageSchemaId'));

    if (damageSchemaIdField) {
      const currentSchema = damageSchemaData?.damageSchemas?.find(
        ({ id }) => id === damageSchemaIdField.value
      );

      setFieldsForSchema(currentSchema as AtlasGqlDamageSchema);
    }
  };

  if (loading) {
    return <Spinner />;
  }

  return (
    <Form
      form={form}
      layout="vertical"
      onFieldsChange={handleFieldChange}
      initialValues={{ damageSchemaId: defaultSchema?.id }}
    >
      <UploadForm
        schemaOptions={schemaOptions}
        dataFields={dataFields}
        setRows={setRows}
        step={step}
        setStep={setStep}
        onClose={handleResetAndClose}
        onClickPreview={handleClickPreview}
        getFieldsValue={form.getFieldsValue}
        resetFields={(fields?: string[]) => form.resetFields(fields)}
        fieldsHidden={step >= STEPS.confirmation}
      />

      {step === STEPS.confirmation && rows.length > 0 && (
        <Confirmation
          dataFields={dataFields}
          headerMapping={headerMapping}
          rows={rows}
          setRows={setRows}
          schema={getSelectedSchema()}
          setStep={setStep}
          handleSubmit={handleSubmit}
          isSubmitting={uploading}
        />
      )}
      {step === STEPS.error &&
        (ErrorStateComponent ? (
          <ErrorStateComponent
            importResult={importResult}
            importedFileName={selectedFiles?.[0]?.name ?? t('inspection.yourFile', 'your file')}
          />
        ) : (
          <ErrorState
            importResult={importResult}
            importedFileName={selectedFiles?.[0]?.name ?? t('inspection.yourFile', 'your file')}
          />
        ))}
    </Form>
  );
};
